import { providerKeyData } from '@src/utility/common.service2';
import {
  checkGeoFlow,
  checkHCO,
  checkIsInternal,
  checkIsSimulationMode,
  checkMultiTin,
  checkSingleTin,
} from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import { MULTI_TIN } from '@src/apps/PriorAuth/constant';

const _httpService = new HttpService();

type CurrentFilterValues = {
  timeperiod?: Record<string, unknown> | null;
};
type GeoOption = { id: string; Name: string; value: string; displayName: string };
type GeoApiResponse = { Data?: unknown };

if (checkIsInternal()) {
  _httpService?.setBaseURL(config('API_DOMAIN_INTERNAL') || '');
} else {
  _httpService?.setBaseURL(config('API_DOMAIN_EXTERNAL') || '');
}

export const MARKET_URL = '/claim-search/topfilter';
export const TAX_STATE_REGION_URL = '/standard-search/topfilter';
export const TOP_FILTER_URL = '/standard-search/topfilter';
export const NPI_FLOW = 'NPI';
export const TIN_FLOW = 'TIN';
export const REQUESTED_JOURNEY = 'priorAuthFilter';
export const NPS_DRIVER_FILTER = 'npsDriverFilter';
export const PSMS_FILTER_JOURNEY = 'psmsFilter';
export const STATE_TEXT = 'STATE';
export const MARKET_TEXT = 'MARKET';
export const MARKET_NAME = 'MARKETNAME';
export const REGION_STATE_INITIAL = 'REGION_STATE_INITIAL';
export const EXTENDED_TIN_DETAIL_URL = '/extended-tins/top-filter?items=MULTITIN';
export const PSNMS_EXTENDED_TIN_DETAIL_JOURNEY = 'provTinListFilterPSMS';

export const getGeoData = async (
  flow: string,
  url: string,
  CurrentVals: CurrentFilterValues,
  geoSelectedVals?: string[] | string
) => {
  const payload = await getPSMSFilterPayload(flow, CurrentVals, geoSelectedVals);
  try {
    const response = (await _httpService.push(url, payload)) as GeoApiResponse;
    if (response?.Data) {
      if (flow === REGION_STATE_INITIAL) {
        const data = response.Data as {
          RegionList: string[];
          StateList: { StateAbbr: string; StateName: string }[];
        };
        const RegionList: GeoOption[] = data.RegionList.map((region: string) => {
          const camelCaseRegion = region
            .toLowerCase()
            .replace(/(?:^|\s|_)(\w)/g, (_, c) => c.toUpperCase());
          return {
            id: region,
            Name: camelCaseRegion,
            value: camelCaseRegion,
            displayName: camelCaseRegion,
          };
        });

        const StateList: GeoOption[] = data.StateList.map((state) => ({
          id: state.StateAbbr,
          Name: state.StateName,
          value: state.StateName,
          displayName: state.StateName,
        }));

        // Sort both RegionList and StateList accordingly
        RegionList.sort((a, b) => {
          const regionA = a.displayName || '';
          const regionB = b.displayName || '';
          return regionA.localeCompare(regionB, undefined, { numeric: true });
        });

        StateList.sort((a, b) => {
          const stateA = a.displayName || '';
          const stateB = b.displayName || '';
          return stateA.localeCompare(stateB, undefined, { numeric: true });
        });

        return { RegionList, StateList };
      } else if (flow === MARKET_TEXT) {
        const MarketList: GeoOption[] = (response.Data as { Name: string }[]).map((market) => ({
          id: market?.Name,
          Name: market?.Name,
          value: market?.Name,
          displayName: market?.Name,
        }));

        // Sort MarketList accordingly
        MarketList.sort((a, b) => {
          const marketA = a.displayName || '';
          const marketB = b.displayName || '';
          return marketA.localeCompare(marketB, undefined, { numeric: true });
        });
        return { MarketList };
      }
    }
  } catch {
    return null;
  }
};

const getTinFilterPayload = (
  timeFilter: string,
  timeFilterText: string,
  providerKey: string,
  selectedVal?: string[] | string
) => {
  return {
    RequestedJourney: '',
    PriorAuthFilter: {
      ProviderSysKey: checkIsInternal() && !checkIsSimulationMode() ? providerKey : undefined,
      ItemsBy: TIN_FLOW,
      TimeFilter: timeFilter,
      TimeFilterText: timeFilterText,
      Tins:
        sessionStorage.getItem('multiTinNavigation') !== 'true'
          ? selectedVal
          : sessionStorage.getItem('multiSelectedTins'),
    },
  };
};

const getRegionStatePayload = (
  timeFilter: string,
  timeFilterText: string,
  selectedVal?: string[] | string
) => {
  const region = selectedVal && selectedVal.length > 0 ? selectedVal[0] : undefined;
  const state = selectedVal && selectedVal.length > 1 ? selectedVal[1] : undefined;
  const affordabilityFilter: Record<string, string> = {
    TimeFilter: timeFilter,
    TimeFilterText: timeFilterText,
  };

  if (region) {
    affordabilityFilter.Region = region;
  }
  if (state) {
    affordabilityFilter.State = state;
  }

  return {
    RequestedJourney: NPS_DRIVER_FILTER,
    PriorAuthAffordabilityFilter: affordabilityFilter,
  };
};

const getMarketPayload = (
  timeFilter: string,
  timeFilterText: string,
  selectedVal?: string[] | string
) => {
  const region = selectedVal && selectedVal.length > 0 ? selectedVal[0] : undefined;
  const state = selectedVal && selectedVal.length > 1 ? selectedVal.slice(1) : [];
  const priorAuthFilter: Record<string, string> = {
    TimeFilter: timeFilter,
    TimeFilterText: timeFilterText,
    ItemsBy: MARKET_NAME,
  };

  if (region) {
    priorAuthFilter.Region = region;
  }
  if (state.length > 0) {
    priorAuthFilter.State = Array.isArray(state) ? state.join(',') : state;
  }

  return {
    RequestedJourney: REQUESTED_JOURNEY,
    PriorAuthFilter: priorAuthFilter,
  };
};

export const getPSMSFilterPayload = async (
  currentFlow: string,
  currentVal?: CurrentFilterValues,
  selectedVal?: string[] | string
) => {
  const providerKey = providerKeyData();
  const standardPeriods = ['Last30Days', 'Last3Months', 'Last6Months', 'Last12Months', 'YTD'];

  // Get timeperiod value - prioritize .value property
  const timePeriodValue =
    typeof currentVal?.timeperiod?.value === 'string'
      ? currentVal.timeperiod.value
      : 'Last12Months';

  const isStandardPeriod = standardPeriods.includes(timePeriodValue);

  const timeFilter = isStandardPeriod ? timePeriodValue : 'CalendarYear';
  const timeFilterText = timePeriodValue;
  switch (currentFlow) {
    case MULTI_TIN: {
      return {
        RequestedJourney: '',
        PriorAuthFilter: {
          TimeFilter: timeFilter,
          TimeFilterText: timeFilterText,
          ItemsBy: TIN_FLOW,
          ProviderSysKey: '',
          Tins: sessionStorage.getItem('multiSelectedTins') || '',
        },
      };
    }
    case TIN_FLOW: {
      return getTinFilterPayload(timeFilter, timeFilterText, providerKey, selectedVal);
    }
    case REGION_STATE_INITIAL: {
      return getRegionStatePayload(timeFilter, timeFilterText, selectedVal);
    }
    case MARKET_TEXT: {
      return getMarketPayload(timeFilter, timeFilterText, selectedVal);
    }
    default: {
      return {
        TimeFilterText: '2025',
        EntityId: 'ENT0002053',
        IsPSMS: '1',
      };
    }
  }
};

export const getPSMSCommonPayload = async () => {
  // const providerKey = providerKeyData();
};

export const getPSMSCurrentFlow = () => {
  if (checkSingleTin()) return 'SINGLE_TIN';
  if (checkMultiTin()) return 'MULTI_TIN';
  if (checkGeoFlow()) return 'GEO_FLOW';
  if (checkHCO()) return 'HCO';
  return undefined;
};

/**
 * Get TIN list for selected entity from top filter API
 * @param entityId - The provider entity ID
 * @returns Promise with TIN list data
 */
export const getTopFilterTins = async (entityId: string) => {
  if (!entityId) {
    return null;
  }

  const payload = {
    RequestedJourney: PSMS_FILTER_JOURNEY,
    PsmsFilter: {
      EntityId: entityId,
    },
  };

  try {
    const response = (await _httpService.push(TOP_FILTER_URL, payload)) as GeoApiResponse;
    if (response?.Data) {
      return response.Data;
    }
    return null;
  } catch (error) {
    console.error('Error calling top filter API:', error);
    return null;
  }
};

export const getExtendedTinDetail = async (tin: string) => {
  if (!tin) {
    return null;
  }

  const payload = {
    RequestedJourney: PSNMS_EXTENDED_TIN_DETAIL_JOURNEY,
    ProvTinListFilter: {
      Tin: tin,
    },
  };

  try {
    const response = (await _httpService.push(EXTENDED_TIN_DETAIL_URL, payload)) as GeoApiResponse;
    if (response?.Data) {
      return response.Data;
    }
    return null;
  } catch (error) {
    console.error('Error calling extended TIN detail API:', error);
    return null;
  }
};
